import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import {
  OrganizationDto,
  OrganizationDetailDto,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
} from "../types/organization";
import { StaffPermissionDto } from "../types/staffPermission";

type ApiResponseV2<T> = {
  success: boolean;
  message: string | null;
  data: T;
};

type ApiResponse = {
  success: boolean;
  message: string | null;
};

const organizationKeys = {
  all: ["organizations"] as const,
  detail: (id: string) => ["organizations", id] as const,
};

// GET /web/organizations — Admin only
export const useOrganizations = () =>
  useQuery({
    queryKey: organizationKeys.all,
    queryFn: async (): Promise<OrganizationDto[]> => {
      const res = await fetch(`${API_URL}/organizations`, {
        credentials: "include",
      });
      const body: ApiResponseV2<OrganizationDto[]> = await res.json();
      return body.data;
    },
  });

// GET /web/organizations/{id} — platform admin, the organization's owner, or a
// staff holding `organization_read`. `enabled` lets a caller hold the request
// back until it actually needs the detail.
export const useOrganization = (id: string, enabled = true) =>
  useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: async (): Promise<OrganizationDetailDto> => {
      const res = await fetch(`${API_URL}/organizations/${id}`, {
        credentials: "include",
      });
      const body: ApiResponseV2<OrganizationDetailDto> = await res.json();
      return body.data;
    },
    enabled: !!id && enabled,
  });

// POST /web/organizations — Admin only
export const useCreateOrganization = (
  onSuccess?: (org: OrganizationDto) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CreateOrganizationRequest
    ): Promise<OrganizationDto> => {
      const res = await fetch(`${API_URL}/organizations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body: ApiResponseV2<OrganizationDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to create organization");
      return body.data;
    },
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      onSuccess?.(org);
    },
  });
};

// PUT /web/organizations/{id}
export const useUpdateOrganization = (
  onSuccess?: (org: OrganizationDto) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateOrganizationRequest;
    }): Promise<OrganizationDto> => {
      const res = await fetch(`${API_URL}/organizations/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body: ApiResponseV2<OrganizationDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to update organization");
      return body.data;
    },
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(org.id),
      });
      onSuccess?.(org);
    },
  });
};

// POST /web/organizations/{id}/restaurants — Admin only
export const useAttachRestaurants = (
  onSuccess?: (org: OrganizationDto) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      restaurantIds,
    }: {
      id: string;
      restaurantIds: string[];
    }): Promise<OrganizationDto> => {
      const res = await fetch(`${API_URL}/organizations/${id}/restaurants`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantIds }),
      });
      const body: ApiResponseV2<OrganizationDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to attach restaurants");
      return body.data;
    },
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(org.id),
      });
      onSuccess?.(org);
    },
  });
};

// DELETE /web/organizations/{id}/restaurants — Admin only
export const useDetachRestaurants = (
  onSuccess?: (org: OrganizationDto) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      restaurantIds,
    }: {
      id: string;
      restaurantIds: string[];
    }): Promise<OrganizationDto> => {
      const res = await fetch(`${API_URL}/organizations/${id}/restaurants`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantIds }),
      });
      const body: ApiResponseV2<OrganizationDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to detach restaurants");
      return body.data;
    },
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(org.id),
      });
      onSuccess?.(org);
    },
  });
};

// GET /web/organizations/{id}/permissions — Owner only (ROLE_STAFF whose id is the org's ownerId)
export const useOrganizationPermissions = (organizationId: string | null | undefined) =>
  useQuery({
    queryKey: ["organization", organizationId, "permissions"] as const,
    queryFn: async (): Promise<StaffPermissionDto[]> => {
      const res = await fetch(`${API_URL}/organizations/${organizationId}/permissions`, {
        credentials: "include",
      });
      const body: ApiResponseV2<StaffPermissionDto[]> = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to fetch organization permissions");
      return body.data;
    },
    enabled: !!organizationId,
  });

// DELETE /web/organizations/{id} — Admin only
export const useDeleteOrganization = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_URL}/organizations/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const body: ApiResponse = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to delete organization");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      onSuccess?.();
    },
  });
};
